import { inject, Injectable, signal } from '@angular/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe } from 'rxjs';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';
import { Network } from '@capacitor/network';
import { Capacitor } from '@capacitor/core';
import { ToastService } from '../toast-service/toast.service';

@Injectable({
    providedIn: 'root',
})
export class NetworkStatusService {
    private readonly toastService = inject(ToastService);

    private hasBeenOffline = false;
    private hasListener = false;
    private hasWebListener = false;

    private readonly connectedState = signal(true);

    /**
     * Whether the device currently reports connectivity. Undebounced so a flow that
     * needs to act the moment the network returns, such as re-acquiring a lock, is
     * not held back by the toast's two-second settle. Optimistically true until the
     * first status event: a wrong "offline" would block work, a wrong "online" only
     * costs one failed request.
     */
    readonly connected = this.connectedState.asReadonly();

    private readonly handleNetworkStatus = rxMethod<boolean>(
        pipe(
            debounceTime(2000),
            distinctUntilChanged(),
            tap((connected) => {
                if (!connected) {
                    this.hasBeenOffline = true;
                    this.toastService.showError('You are offline');
                } else if (this.hasBeenOffline) {
                    this.toastService.showSuccess('You are back online');
                }
            }),
        ),
    );

    private readonly onWebOnline = (): void => this.connectedState.set(true);
    private readonly onWebOffline = (): void => this.connectedState.set(false);

    /**
     * Starts tracking connectivity. Native platforms use the Capacitor Network plugin and
     * keep the offline/online toasts; the browser falls back to the window online and
     * offline events, which only feed {@link connected} so web users see no new toasts.
     */
    initialize(): void {
        if (!Capacitor.isNativePlatform()) {
            this.initializeWeb();
            return;
        }

        if (this.hasListener) return;

        this.hasListener = true;
        void Network.getStatus()
            .then((status) => this.connectedState.set(status.connected))
            .catch(() => undefined);
        Network.addListener('networkStatusChange', (status) => {
            this.connectedState.set(status.connected);
            this.handleNetworkStatus(status.connected);
        });
    }

    stopListening(): void {
        if (this.hasWebListener) {
            window.removeEventListener('online', this.onWebOnline);
            window.removeEventListener('offline', this.onWebOffline);
            this.hasWebListener = false;
        }

        if (!this.hasListener) return;

        Network.removeAllListeners();
        this.hasListener = false;
        this.hasBeenOffline = false;
    }

    private initializeWeb(): void {
        if (this.hasWebListener || typeof window === 'undefined') return;

        this.hasWebListener = true;
        this.connectedState.set(typeof navigator === 'undefined' || navigator.onLine !== false);
        window.addEventListener('online', this.onWebOnline);
        window.addEventListener('offline', this.onWebOffline);
    }
}
